import React, { useMemo } from 'react';
import EditDrawer from './EditDrawer';
import Field from '../../Field';
import TagPickerField from '../TagPickerField';
import { STUDENT_TEXT_MAX } from '../../../domain/people';
import { TAG_KINDS, tagKind } from '../../../domain/tags';
import { timeZoneOptions } from '../../../lib/intlOptions';
import s from './editStudent.module.css';
import { DateInput } from '../../RegionInputs';
import SmsUnavailable, { smsOffClass } from '../../SmsUnavailable';
import { useSmsAvailable } from '../../../context/SmsGateContext';
import { smsShown } from '../../../domain/smsGate';

// Drawer 2 of five — the pencil on the Student Details tab (#120).
//
// The slice the profile pencil does not reach: where the student came from,
// what they play, and whether they are reminded. Its fields and their order
// are MMS's.
//
// MMS titles this drawer and the profile drawer both "Edit Student"; this one
// is "Student Details", the card its pencil sits on, so two drawers on one
// page are told apart.
//
// Time Zone writes the family, not the student: MMS's form says "This time
// zone will also apply to all contacts and Students in this student's
// Family." One zone per account, asked for where the teacher is standing, so
// saving this drawer writes two documents (`patches` in
// hooks/useStudentEdit.js, domain/people.js). Shown only when the studio has
// turned on Multi-Time Zone Selection, as MMS gates it and as the wizard's
// family step does.
export default function EditStudentDetailsDrawer({
  open, form, errors, saving, multiTimeZone = false, hasFamily = true,
  tags = [], tagsLoaded = true, onToggleTag = () => {}, onCreateTag = () => {},
  onChange, onClose, onSave,
}) {
  // Four hundred-odd options, derived from Intl once and cached in the module,
  // memoised so the list is not rebuilt on every keystroke. The hook runs
  // before the early return, so the hook order does not change between
  // renders.
  const zones = useMemo(() => (multiTimeZone ? timeZoneOptions() : []), [multiTimeZone]);
  const smsAvailable = useSmsAvailable();

  if (!open) return null;

  const text = (key, label, max) => (
    <Field label={label} htmlFor={`edit-${key}`} hint="Optional" error={errors[key]}>
      <input
        id={`edit-${key}`} className={s.input} maxLength={max}
        value={form[key]} onChange={(e) => onChange(key, e.target.value)}
      />
    </Field>
  );

  // ── The two tag pickers (#328) ────────────────────────────────────────────
  //
  // The wizard's control over the same managed tags, with the same
  // create-inline box and duplicate refusal, so a tag is never a string on the
  // student document outside the Tags tab's rename, recolour and merge. #121's
  // bounds on the student's tag lists (`STUDENT_TAGS_MAX`,
  // `STUDENT_TAG_CHARS_MAX`) are not restated: nothing writes those lists from
  // here, and `firestore.rules` still carries them for documents that have
  // one.
  //
  // Unlike the wizard this unticks: an existing student leaves tags, and the
  // write half is `tagWrites` in hooks/useStudentEdit.js.
  const selected = new Set(form.tagIds || []);
  const allTags = [...tags, ...(form.newTags || [])];
  const picker = (kind, label, kindLabel) => (
    <TagPickerField
      label={label}
      kind={kind}
      kindLabel={kindLabel}
      tags={allTags.filter((t) => tagKind(t) === kind)}
      allTags={allTags}
      // "This studio has no tags" and "the tags have not arrived" are the same
      // empty array and are not the same sentence — see TagPickerField's header.
      loaded={tagsLoaded}
      selected={selected}
      onToggle={onToggleTag}
      onCreate={(name) => onCreateTag(name, kind)}
    />
  );

  return (
    <EditDrawer open title="Student Details" saving={saving} onClose={onClose} onSave={onSave}>
      {multiTimeZone ? (
        <div className={[s.spaced, s.medium].join(' ')}>
          <Field
            label="Time Zone"
            htmlFor="edit-time-zone"
            hint={hasFamily
              ? 'Applies to everyone on this family account'
              : 'This student has no family account, so there is nowhere to store a zone.'}
          >
            <select
              id="edit-time-zone"
              className={s.input}
              disabled={!hasFamily}
              value={form.timeZone}
              onChange={(e) => onChange('timeZone', e.target.value)}
            >
              <option value="">Same as the studio</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
            </select>
          </Field>
        </div>
      ) : null}

      <div className={[s.spaced, s.medium].join(' ')}>
        <Field label="Student Since" htmlFor="edit-student-since" hint="Optional">
          <DateInput
            id="edit-student-since" className={s.input}
            value={form.studentSince} onChange={(e) => onChange('studentSince', e.target.value)}
          />
        </Field>
      </div>

      <div className={s.pair}>
        {text('school', 'School', STUDENT_TEXT_MAX.school)}
        {text('referrer', 'Referrer', STUDENT_TEXT_MAX.referrer)}
      </div>
      <div className={s.spaced}>
        {text('skillLevel', 'Skill Level', STUDENT_TEXT_MAX.skillLevel)}
      </div>

      <div className={s.spaced}>
        {picker(TAG_KINDS.instrument, 'Instrument Tags', 'Instrument Tag')}
      </div>
      <div className={s.spaced}>
        {picker(TAG_KINDS.group, 'Group Tags', 'Group Tag')}
      </div>
      {/* The one refusal here about data rather than typing: the tags
          collection has not been read, so `saveTag` would drop a membership
          write silently (`refusals` in hooks/useStudentEdit.js). */}
      {errors.tagIds ? <div className={s.tagError}>{errors.tagIds}</div> : null}

      {/* Two questions, because a family may want the email and not the text:
          WANTS_REMINDER in domain/reminders.js reads a flag per channel.
          Neither is the SMS Capable tick on the profile drawer — that is
          consent to text a number, this a wish to be reminded — and a text goes
          only where both are ticked. */}
      <div className={s.groupLabelSpaced}>Student Lesson Reminders</div>
      <label className={s.checkRow}>
        <input
          type="checkbox" checked={form.emailReminders}
          onChange={(e) => onChange('emailReminders', e.target.checked)}
        />
        <span>Send email lesson reminders</span>
      </label>
      {/* Disabled and drawn off while texts cannot be sent (#782,
          domain/smsGate.js). A saved tick stays saved: the form holds it and
          Save writes it back unchanged. */}
      <label className={smsAvailable ? s.checkRow : `${s.checkRow} ${smsOffClass}`}>
        <input
          type="checkbox" checked={smsShown(form.smsReminders, smsAvailable)}
          disabled={!smsAvailable}
          onChange={(e) => { if (smsAvailable) onChange('smsReminders', e.target.checked); }}
        />
        <span>Send SMS lesson reminders</span>
      </label>
      {smsAvailable ? null : <SmsUnavailable />}
      {/* MMS's own sentence, near enough: the lead time lives on the event
          category and every category ships with reminders off (#87), so a
          tick above is one of two gates. */}
      <div className={s.hint}>
        This student will only receive reminders for categories with reminders
        enabled — set a lead time under Calendar &rarr; Options &rarr;
        Categories &amp; Locations.
      </div>
    </EditDrawer>
  );
}
